import React, { useState, useEffect } from 'react';
import { getWhatsAppMessages, sendWhatsAppMessage, getWhatsAppTemplates, getCustomers } from '../lib/api';
import { WHATSAPP } from '../constants/testIds';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Send, MessageCircle, User, Phone, Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';

export const WhatsApp = () => {
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    phone: '',
    message: '',
    template: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [messagesRes, templatesRes, customersRes] = await Promise.all([
        getWhatsAppMessages(),
        getWhatsAppTemplates(),
        getCustomers(),
      ]);
      setMessages(messagesRes.data);
      setTemplates(templatesRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      phone: '',
      message: '',
      template: '',
    });
  };

  const handleCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setFormData({
      ...formData,
      customer_id: customerId,
      phone: customer?.phone || '',
    });
    setSelectedCustomer(customer);
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      let message = template.message;
      if (selectedCustomer) {
        message = message.replace('{customer_name}', selectedCustomer.name);
      }
      setFormData({
        ...formData,
        template: templateId,
        message: message,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await sendWhatsAppMessage(formData);
      toast.success('Message sent successfully (Mock)');
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'Unknown';
  };

  const filterMessagesByCustomer = (customerId) => {
    return messages.filter(m => m.customer_id === customerId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  // Group messages by customer
  const customerMessages = customers.reduce((acc, customer) => {
    const customerMsgs = filterMessagesByCustomer(customer.id);
    if (customerMsgs.length > 0) {
      acc.push({ customer, messages: customerMsgs });
    }
    return acc;
  }, []);

  return (
    <div data-testid={WHATSAPP.container} className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight" style={{ color: '#84bd0e' }}>WhatsApp Integration</h1>
          <p className="text-muted-foreground mt-1">Send messages and view conversation history</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid={WHATSAPP.sendBtn} onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-slate-900 hover:bg-slate-800">
              <Send className="w-4 h-4 mr-2" />
              Send Message
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-outfit">Send WhatsApp Message</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Customer *</Label>
                <Select value={formData.customer_id} onValueChange={handleCustomerSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name} - {c.phone}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+91 9876543210"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Use Template</Label>
                <Select value={formData.template} onValueChange={handleTemplateSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Message *</Label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Type your message..."
                  rows={4}
                  required
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                <strong>Note:</strong> This is a mock WhatsApp integration. Messages are stored locally and not actually sent.
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={submitting}>
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Message
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Templates */}
        <Card className="border-slate-200">
          <CardHeader className="border-b border-slate-200">
            <CardTitle className="font-outfit text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Message Templates
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            <div className="space-y-3">
              {templates.map((t) => (
                <div key={t.id} className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium text-slate-900 text-sm">{t.name}</p>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{t.message}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Message History */}
        <Card className="lg:col-span-2 border-slate-200">
          <CardHeader className="border-b border-slate-200">
            <CardTitle className="font-outfit text-lg flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              Message History
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[500px]" data-testid={WHATSAPP.messageList}>
              {customerMessages.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {customerMessages.map(({ customer, messages: msgs }) => (
                    <div key={customer.id} className="p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{customer.name}</p>
                          <p className="text-xs text-slate-500 flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {customer.phone}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2 pl-13">
                        {msgs.map((msg) => (
                          <div
                            key={msg.id}
                            className={`max-w-[80%] p-3 rounded-lg ${
                              msg.direction === 'outgoing'
                                ? 'bg-slate-900 text-white ml-auto'
                                : 'bg-slate-100 text-slate-900'
                            }`}
                          >
                            <p className="text-sm">{msg.message}</p>
                            <p className={`text-xs mt-1 ${msg.direction === 'outgoing' ? 'text-slate-400' : 'text-slate-500'}`}>
                              {new Date(msg.created_at).toLocaleString()}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <MessageCircle className="w-12 h-12 mb-4 text-slate-300" />
                  <p>No messages yet</p>
                  <p className="text-sm">Click "Send Message" to start a conversation</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
